import { useRef, useState, type ChangeEvent } from "react";
import {
  ArrowDown,
  ArrowUp,
  ImagePlus,
  LoaderCircle,
  RefreshCcw,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ProjectArtstyleReferenceView } from "@/features/image-generation";
import { cn } from "@/lib/utils";

type ArtstyleReferenceManagerProps = {
  busy: boolean;
  maxReferences: number;
  onAddReference: (file: File) => Promise<void>;
  onRemoveReference: (referencePath: string) => Promise<void>;
  onReorderReferences: (orderedReferencePaths: string[]) => Promise<void>;
  onReplaceReference: (referencePath: string, file: File) => Promise<void>;
  references: ProjectArtstyleReferenceView[];
  variant?: "card" | "sheet";
};

export function ArtstyleReferenceManager({
  busy,
  maxReferences,
  onAddReference,
  onRemoveReference,
  onReorderReferences,
  onReplaceReference,
  references,
  variant = "card",
}: ArtstyleReferenceManagerProps) {
  const { t } = useTranslation();
  const isSheetVariant = variant === "sheet";
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingReplacePath, setPendingReplacePath] = useState<string | null>(null);
  const remainingSlots = Math.max(maxReferences - references.length, 0);

  function openPicker(referencePath: string | null) {
    if (busy) {
      return;
    }

    setPendingReplacePath(referencePath);
    fileInputRef.current?.click();
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      setPendingReplacePath(null);
      return;
    }

    if (pendingReplacePath) {
      await onReplaceReference(pendingReplacePath, file);
      setPendingReplacePath(null);
      return;
    }

    await onAddReference(file);
  }

  function moveReference(referencePath: string, direction: -1 | 1) {
    const orderedReferencePaths = references.map((reference) => reference.path);
    const currentIndex = orderedReferencePaths.indexOf(referencePath);
    const nextIndex = currentIndex + direction;

    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= orderedReferencePaths.length) {
      return;
    }

    const [movedReferencePath] = orderedReferencePaths.splice(currentIndex, 1);
    orderedReferencePaths.splice(nextIndex, 0, movedReferencePath!);
    void onReorderReferences(orderedReferencePaths);
  }

  return (
    <section
      className={cn(
        "space-y-4",
        isSheetVariant
          ? "border-t border-ink/10 pt-6"
          : "rounded-[28px] border border-ink/10 bg-canvas/65 p-5",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-foreground">
              {t("projects.image.references.title")}
            </p>
            <Badge variant="outline">
              {t("projects.image.references.count", {
                count: references.length,
                max: maxReferences,
              })}
            </Badge>
          </div>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            {t("projects.image.references.body")}
          </p>
        </div>

        <Button
          disabled={busy || remainingSlots === 0}
          onClick={() => openPicker(null)}
          type="button"
          variant="outline"
        >
          {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {t("projects.image.references.add")}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        type="file"
      />

      {remainingSlots > 0 ? (
        <p
          className={cn(
            "text-sm text-muted-foreground",
            isSheetVariant
              ? ""
              : "rounded-[20px] border border-dashed border-border/70 bg-secondary/35 px-4 py-3",
          )}
        >
          {t("projects.image.references.remaining", { count: remainingSlots })}
        </p>
      ) : (
        <p
          className={cn(
            "text-sm text-amber-950",
            isSheetVariant
              ? ""
              : "rounded-[20px] border border-amber-500/25 bg-amber-50/80 px-4 py-3",
          )}
        >
          {t("projects.image.references.limitReached")}
        </p>
      )}

      <div className="space-y-3">
        {references.length ? (
          references.map((reference, index) => (
            <article
              key={reference.path}
              className={cn(
                isSheetVariant
                  ? "border-t border-ink/10 pt-4 first:border-t-0 first:pt-0"
                  : "rounded-[24px] border border-ink/10 bg-background/75 p-4",
              )}
            >
              <div className="grid gap-4 sm:grid-cols-[7.5rem_minmax(0,1fr)]">
                {reference.previewUrl ? (
                  <img
                    alt={reference.fileName}
                    className="h-28 w-full rounded-[20px] object-cover"
                    src={reference.previewUrl}
                  />
                ) : (
                  <div className="flex h-28 items-center justify-center rounded-[20px] border border-dashed border-border bg-secondary/25 px-4 text-center text-sm text-muted-foreground">
                    {t("projects.image.references.previewUnavailable")}
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <p className="truncate text-sm font-semibold text-foreground">
                        {reference.fileName}
                      </p>
                      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                        {t("projects.image.references.position", {
                          index: index + 1,
                          count: references.length,
                        })}
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {t("projects.image.references.anchorLabel", {
                        index: index + 1,
                      })}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      disabled={busy}
                      onClick={() => openPicker(reference.path)}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <RefreshCcw className="h-4 w-4" />
                      {t("projects.image.references.replace")}
                    </Button>
                    <Button
                      disabled={busy || index === 0}
                      onClick={() => moveReference(reference.path, -1)}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <ArrowUp className="h-4 w-4" />
                      {t("projects.image.references.moveUp")}
                    </Button>
                    <Button
                      disabled={busy || index === references.length - 1}
                      onClick={() => moveReference(reference.path, 1)}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      <ArrowDown className="h-4 w-4" />
                      {t("projects.image.references.moveDown")}
                    </Button>
                    <Button
                      disabled={busy}
                      onClick={() => void onRemoveReference(reference.path)}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("projects.image.references.remove")}
                    </Button>
                  </div>
                </div>
              </div>
            </article>
          ))
        ) : (
          <div
            className={cn(
              "text-sm leading-6 text-muted-foreground",
              isSheetVariant
                ? ""
                : "rounded-[24px] border border-dashed border-border bg-secondary/20 px-4 py-5",
            )}
          >
            {t("projects.image.references.empty")}
          </div>
        )}
      </div>
    </section>
  );
}
